import type { Point } from '../../hex/types';

/**
 * Creates a closed path for a hex based on its vertices.
 * Used by multiple render passes to avoid duplicated path logic.
 */
export function hexPath(ctx: CanvasRenderingContext2D, verts: Point[]): void {
    ctx.beginPath();
    ctx.moveTo(verts[0].x, verts[0].y);
    for (let i = 1; i < verts.length; i += 1) ctx.lineTo(verts[i].x, verts[i].y);
    ctx.closePath();
}

/**
 * Computes the centroid of a hex from its vertices.
 * Used for lighting, gradients, and positioning.
 */
export function hexCenter(verts: Point[]): Point {
    let x = 0;
    let y = 0;
    for (const v of verts) {
        x += v.x;
        y += v.y;
    }
    return { x: x / verts.length, y: y / verts.length };
}

/**
 * Builds per-edge geometry with outward normals for edge-aware rendering.
 */
export function edgeDirs(verts: Point[], center: Point) {
    const edges: { a: Point; b: Point; mid: Point; out: Point }[] = [];
    for (let i = 0; i < verts.length; i += 1) {
        const a = verts[i];
        const b = verts[(i + 1) % verts.length];
        const mid = { x: (a.x + b.x) * 0.5, y: (a.y + b.y) * 0.5 };
        const vx = mid.x - center.x;
        const vy = mid.y - center.y;
        const len = Math.hypot(vx, vy) || 1;
        edges.push({ a, b, mid, out: { x: vx / len, y: vy / len } });
    }
    return edges;
}

/**
 * Picks the edge index that faces a target point from the center.
 */
export function edgeIndexToward(verts: Point[], center: Point, target: Point): number {
    const edges = edgeDirs(verts, center);
    const dx = target.x - center.x;
    const dy = target.y - center.y;
    const len = Math.hypot(dx, dy) || 1;
    const dir = { x: dx / len, y: dy / len };

    let best = 0;
    let bestDot = -Infinity;
    for (let i = 0; i < edges.length; i += 1) {
        const d = edges[i].out.x * dir.x + edges[i].out.y * dir.y;
        if (d > bestDot) {
            bestDot = d;
            best = i;
        }
    }
    return best;
}
